import React, { useState, useEffect, useRef } from 'react';
import firebase from "firebase/app";
import "firebase/storage";

// Function to get display URL for images (handles both legacy and new)
const getDisplayUrl = async (imageUrl, questionId = null) => {
  if (!imageUrl) {
    return '';
  }
  
  let finalUrl;
  
  // If it's a short filename (just check if it's not a full URL)
  if (!imageUrl.includes('firebase') && !imageUrl.startsWith('data:') && !imageUrl.startsWith('http')) {
    // CORRECT: Use database questionId for storage path
    // Storage path should be: image-questions/{questionId}/{filename}
    const fullPath = `image-questions/${imageUrl}`;
    
    try {
      const storageRef = firebase.storage().ref(fullPath);
      finalUrl = await storageRef.getDownloadURL(); // ✅ ONLY ONE CALL
    } catch (error) {
      // If the direct path fails, the image might be stored elsewhere
      // This indicates a migration issue where the database doesn't match storage
      finalUrl = imageUrl; // Fallback to original
    }
  } else if (imageUrl.startsWith('q-') || imageUrl.startsWith('img_')) {
    finalUrl = imageUrl;
  } else {
    finalUrl = imageUrl;
  }

  return finalUrl;
};

// Authenticated Image Component with Lazy Loading
const AuthenticatedImage = ({ imageUrl, alt, style, questionId }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    // Intersection Observer for lazy loading
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '50px' }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadImage = async () => {
      if (!isVisible) return; // Only load when visible
      
      try {
        setLoading(true);
        setError(false);
        
        const authenticatedUrl = await getDisplayUrl(imageUrl, questionId);
        setSrc(authenticatedUrl);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageUrl && isVisible) {
      loadImage();
    }
  }, [imageUrl, questionId, isVisible]);

  if (loading) {
    return <div ref={imgRef} style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>Loading image...</div>;
  }

  if (error) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#ffebee', color: '#d32f2f', fontSize: '12px', textAlign: 'center', padding: '8px' }}>
      📁 Image not found<br/>
      File may have been deleted or never uploaded
    </div>;
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      style={{
        ...style,
        // Enhanced image styling for better display
        objectFit: 'contain', // Prevents stretching, maintains aspect ratio
        width: '100%',
        height: 'auto',
        maxHeight: style?.maxHeight || '650px', // Increased from 500px to 650px (30% larger)
        display: 'block',
        margin: '0 auto', // Center the image
        // Ensure images are large and visible
        minHeight: '300px', // Increased minimum height for better visibility
        backgroundColor: '#f8f9fa', // Light background for better contrast
        border: '1px solid #e0e0e0',
        borderRadius: '8px',
        boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
        // Smooth transitions
        transition: 'all 0.3s ease',
        // Hover effect for better UX
        cursor: 'pointer'
      }}
      onError={(e) => {
        setError(true);
        e.target.style.display = 'none';
      }}
      onClick={() => {
        // Optional: Open image in full screen on click
        if (src) {
          const newWindow = window.open();
          newWindow.document.write(`
            <html>
              <head>
                <title>Image Preview</title>
                <style>
                  body { 
                    margin: 0; 
                    padding: 20px; 
                    background: #000; 
                    display: flex; 
                    justify-content: center; 
                    align-items: center; 
                    min-height: 100vh;
                  }
                  img { 
                    max-width: 100%; 
                    max-height: 100%; 
                    object-fit: contain;
                    border-radius: 8px;
                    box-shadow: 0 8px 32px rgba(255,255,255,0.1);
                  }
                </style>
              </head>
              <body>
                <img src="${src}" alt="${alt}" />
              </body>
            </html>
          `);
        }
      }}
      title="Click to view full size"
    />
  );
};

export default AuthenticatedImage;
